// ============================================================================
// TOASTS — rendu DOM. Pendant exact de `@orgazone/pos-shell/toast` (React Native).
//
// ⚠️ LES DEUX RENDUS LISENT LA MÊME SPÉCIFICATION (`@orgazone/ui-tokens`) : géométrie, durées, animation,
// empilement, ton → icône. Une modification esthétique se fait DANS LES TOKENS et s'applique aux deux
// plateformes — personne n'a à « penser à aligner l'autre ».
//
// ⚠️ SIGNATURE IDENTIQUE à celle de la tablette (`showToast(msg, tone?, durationMs?)`, SPEC §24.1quater D) :
// un écran porté d'une plateforme à l'autre ne change pas de comportement, et un développeur qui passe de
// l'une à l'autre ne réapprend rien.
//
// CE QUE CE MODULE CORRIGE au passage : le web n'affichait qu'UN SEUL toast — une deuxième action
// effaçait le message de la première, avant même qu'il ait été lu (§23.0). Il empile désormais, comme la
// tablette l'a toujours fait.
// ============================================================================
import { useEffect, useState, type ReactNode } from 'react';
import { TOAST_DEFAULT_MODE, TOAST_ICON, TOAST_PILL, TOAST_SHADOW, TOAST_STACK, TOAST_TIMING, type IconName, type ToastMode, type ToastTone } from '@orgazone/ui-tokens';
import { Icon } from './icons';

export type { ToastTone, ToastMode };

interface ToastItem {
  id: number;
  msg: string;
  tone: ToastTone;
}

// ⚠️ Store MODULE (hors React) et non un contexte : un toast doit pouvoir partir d'un service, d'un
// intercepteur HTTP ou d'un `catch` — c'est-à-dire d'endroits qui n'ont pas de hook sous la main. C'est
// aussi le choix de la tablette : même API, même contrainte.
let items: ToastItem[] = [];
const listeners = new Set<(l: ToastItem[]) => void>();
const notify = (): void => listeners.forEach((fn) => fn([...items]));

/**
 * S'abonne à la file (l'hôte l'utilise ; les tests aussi).
 * ⚠️ Exporté DÉLIBÉRÉMENT : une file qui peut perdre un message de l'utilisateur doit être observable
 * autrement qu'en montant un composant. Sans ce point d'entrée, les tests de la file se réduiraient à
 * « ça n'a pas planté » — c'est-à-dire à rien.
 */
export function subscribeToasts(fn: (l: ToastItem[]) => void): () => void {
  listeners.add(fn);
  fn([...items]);
  return () => { listeners.delete(fn); };
}

export type { ToastItem };

/**
 * Affiche un toast depuis n'importe où. Nécessite un `<ToastHost/>` monté ; sans hôte, l'appel est
 * ignoré silencieusement — jamais d'exception, un message d'information ne doit pas casser un flux.
 * ⚠️ Un toast annonce une RÉUSSITE. Une erreur va en alerte bloquante ou sous le champ concerné : une
 * erreur qui s'efface toute seule est une erreur perdue (§24.1quater A).
 *
 * ⚠️ `mode` — `stack` (défaut) empile, `replace` ne laisse que ce message à l'écran. Le second sert au
 * GESTE RÉPÉTÉ dont seul le dernier retour compte (bipper à la douchette) ; ailleurs il effacerait un
 * message pas encore lu. Le vocabulaire et la règle vivent dans `@orgazone/ui-tokens`, comme le reste du
 * design du toast — les deux plateformes lisent la même définition.
 */
export function showToast(
  msg: string, tone: ToastTone = 'ok', durationMs: number = TOAST_TIMING.defaultMs,
  mode: ToastMode = TOAST_DEFAULT_MODE,
): void {
  const id = Date.now() + Math.random();
  // ⚠️ LES ÉCHÉANCES DES REMPLACÉS RESTENT ARMÉES, et c'est sans effet : chacune filtre par SON id, donc
  //    elle ne trouve plus rien à retirer. Les annuler demanderait de tenir une table de timers pour
  //    rien.
  const vivants = mode === 'replace' ? [] : items;
  items = [...vivants, { id, msg, tone }].slice(-TOAST_STACK.maxVisible);
  notify();
  const handle = setTimeout(() => {
    items = items.filter((x) => x.id !== id);
    notify();
  }, durationMs);
  // ⚠️ Sous Node (tests), un timer d'auto-fermeture RETIENT le process — des toasts cumulés font
  // « le process ne s'est pas terminé ». `unref` n'existe pas dans un navigateur → garde conditionnelle.
  (handle as unknown as { unref?: () => void }).unref?.();
}

/** Signature du rendu d'icône fourni par l'app hôte (chaque SPA a son propre registre). */
export type ToastIconRenderer = (name: IconName, size: number, color: string) => ReactNode;

export interface ToastHostProps {
  /** Rend une icône par son nom — défaut : le registre PARTAGÉ (`ui-web/Icon`, trait du token). */
  renderIcon?: ToastIconRenderer;
  /**
   * Couleur d'icône par ton. ⚠️ DÉFAUT CANONIQUE = teintes de BASE des accents (`--ok`, `--blue`,
   * `--amber`, `--rose`), INDÉPENDANTES du thème — la pilule s'INVERSE par rapport à la page (sombre en
   * clair, claire en sombre) : des tons qui suivraient le thème de la page seraient à CONTRE-PIED de
   * leur fond (icônes délavées — bug Console corrigé 2026-08-05). Même approche que la tablette.
   */
  toneColor?: Record<ToastTone, string>;
  /** Fond de la pilule et couleur du texte — défauts : rôles inversés `--ink-950` / `--paper`. */
  background?: string;
  color?: string;
}

const DEFAULT_TONE_COLOR: Record<ToastTone, string> = {
  ok: 'var(--ok)', info: 'var(--blue)', warn: 'var(--amber)', err: 'var(--rose)',
};
const defaultRenderIcon: ToastIconRenderer = (name, size, color) => (
  <Icon name={name} size={size} color={color} stroke={TOAST_PILL.iconStroke} />
);

function ToastPill({ item, p }: { item: ToastItem; p: ToastHostProps }) {
  const [shown, setShown] = useState(false);
  // Monté à l'échelle réduite puis relâché au tick suivant → même « pop » que l'animation de la tablette.
  useEffect(() => {
    const h = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(h);
  }, []);
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: TOAST_PILL.gap,
        background: p.background ?? 'var(--ink-950)', color: p.color ?? 'var(--paper)',
        padding: `${TOAST_PILL.padV}px ${TOAST_PILL.padH}px`,
        borderRadius: TOAST_PILL.radius,
        maxWidth: TOAST_PILL.maxWidth,
        fontSize: TOAST_PILL.fontSize, fontWeight: TOAST_PILL.fontWeight,
        boxShadow: `0 ${TOAST_SHADOW.offsetY}px ${TOAST_SHADOW.blur}px rgba(0,0,0,${TOAST_SHADOW.opacity})`,
        opacity: shown ? 1 : 0,
        transform: `scale(${shown ? 1 : TOAST_TIMING.enterScaleFrom})`,
        transition: `opacity ${TOAST_TIMING.enterMs}ms ease-out, transform ${TOAST_TIMING.enterMs}ms ease-out`,
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
        {(p.renderIcon ?? defaultRenderIcon)(TOAST_ICON[item.tone], TOAST_PILL.iconSize, (p.toneColor ?? DEFAULT_TONE_COLOR)[item.tone])}
      </span>
      <span style={{
        flexShrink: 1, minWidth: 0,
        display: '-webkit-box', WebkitLineClamp: TOAST_PILL.maxLines, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {item.msg}
      </span>
    </div>
  );
}

/** Hôte des toasts — à monter UNE fois près de la racine de l'app. */
export function ToastHost(props: ToastHostProps = {}) {
  const [list, setList] = useState<ToastItem[]>(() => [...items]);
  // `subscribeToasts` rejoue l'état courant à l'abonnement → rattrape un toast déjà en vol au montage.
  useEffect(() => subscribeToasts(setList), []);
  if (list.length === 0) return null;
  return (
    <div
      // `aria-live` : un toast est une information passagère ; sans annonce, un lecteur d'écran ne la
      // verra jamais passer. `polite` pour ne pas couper la lecture en cours.
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', left: '50%', bottom: TOAST_STACK.bottom, transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: TOAST_STACK.gap,
        zIndex: 1200, pointerEvents: 'none',
      }}
    >
      {list.map((item) => <ToastPill key={item.id} item={item} p={props} />)}
    </div>
  );
}

/** Vide la file — réservé aux TESTS (isole les cas les uns des autres). */
export function __resetToasts(): void {
  items = [];
  notify();
}
